import {FC, useState} from 'react';
import {Alert, Button, TextField, Typography} from '@mui/material';
import {useForm} from 'react-hook-form';
import {useMutation} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import Modal from 'components/Modal/Modal';
import {ENABLE_COUNTRY} from 'graphql/absences/Mutations';

interface EnableCountryModalProps {
  open: boolean;
  countryCode: string;
  countryName: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface EnableFormValues {
  reason: string;
}

/**
 * Single-step modal for re-enabling a previously disabled country configuration.
 * Requires a reason of at least 10 characters before allowing submission.
 *
 * @param open - Whether the modal is visible.
 * @param countryCode - ISO code of the country to enable.
 * @param countryName - Human-readable country name for display.
 * @param onClose - Callback invoked when the modal is dismissed.
 * @param onSuccess - Callback invoked after the country is successfully enabled.
 */
const EnableCountryModal: FC<EnableCountryModalProps> = ({
  open,
  countryCode,
  countryName,
  onClose,
  onSuccess,
}) => {
  const {t} = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {register, watch, reset} = useForm<EnableFormValues>({
    defaultValues: {reason: ''},
  });

  const reason = watch('reason');
  const isReasonValid = reason.trim().length >= 10;

  const [enableCountry] = useMutation(ENABLE_COUNTRY);

  const handleClose = () => {
    reset();
    setErrorMessage(null);
    onClose();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await enableCountry({
        variables: {
          countryCode,
          reason: reason.trim(),
        },
      });
      reset();
      onSuccess();
      onClose();
    } catch (err: unknown) {
      // Show backend error message or fallback to generic error
      const message = err instanceof Error ? err.message : undefined;
      setErrorMessage(message ?? t('leaves.EnableCountryModal.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('leaves.EnableCountryModal.title')}
      onClose={handleClose}
      small
    >
      <div className="flex flex-col gap-4">
        <Typography variant="body2">{countryName}</Typography>
        <TextField
          label={t('leaves.EnableCountryModal.reasonLabel')}
          placeholder={t('leaves.EnableCountryModal.reasonPlaceholder')}
          multiline
          rows={3}
          fullWidth
          {...register('reason')}
        />
        {reason.length > 0 && !isReasonValid && (
          <Typography variant="caption" color="error">
            {t('leaves.EnableCountryModal.reasonMinLength')}
          </Typography>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{mt: 1}}>
            {errorMessage}
          </Alert>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outlined" onClick={handleClose}>
            {t('leaves.EnableCountryModal.cancelButton')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!isReasonValid || isSubmitting}
          >
            {t('leaves.EnableCountryModal.submitButton')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EnableCountryModal;
